import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { minutes, parseYmd, shortDate, today, weekLabel, weekStartOf } from '../../lib/dates';
import type { Assignment, Pass, Progress } from '../../lib/types';
import { clock, minReadSeconds } from '../../lib/reading';
import { needsReadingTime, storyModesUsed, storyPlanLabel } from '../../lib/storyLesson';
import { KIND, byDue, isOpen, isOut, passWord, requirement } from '../../lib/kinds';
import { useClassroom } from '../../state/Classroom';

// Who has done what. A row per student, a column per assignment: by default
// every assignment still open (sent out and not past its due date, or kept open
// for someone by an extension), soonest due first; or the past ones, a week at a
// time by the week they were due (Monday to Sunday).
// "Done" is the first completion; time and visits keep counting after it.

export default function ProgressPage() {
  const { hydrated, assignments, students, progressFor, passesFor, refresh, dueFor } = useClassroom();
  // 'open', or the Monday of a week whose past-due assignments to show.
  const [show, setShow] = useState<string>('open');
  const [refreshing, setRefreshing] = useState(false);
  const t = today();
  const open = useMemo(
    () => assignments.filter(a => isOpen(a, t, students.map(s => dueFor(a, s.user_id)))).sort(byDue),
    [assignments, students, dueFor, t],
  );
  const past = useMemo(
    () => assignments.filter(a => isOut(a, t) && !open.includes(a)).sort(byDue),
    [assignments, open, t],
  );
  // Past assignments by the week they were due, most recent week first.
  const pastWeeks = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of past) {
      const wk = a.due_date ? weekStartOf(parseYmd(a.due_date)) : a.week_start;
      m.set(wk, [...(m.get(wk) ?? []), a]);
    }
    return [...m.entries()].sort((x, y) => y[0].localeCompare(x[0]));
  }, [past]);

  if (!hydrated) return <Loading />;

  const cols = show === 'open' ? open : pastWeeks.find(([w]) => w === show)?.[1] ?? [];

  return (
    <div>
      <div className="row">
        <h1>Progress</h1>
        <span className="spacer" />
        <select style={{ width: 'auto' }} value={show} onChange={e => setShow(e.target.value)} aria-label="Which assignments">
          <option value="open">Open now ({open.length})</option>
          {pastWeeks.length > 0 && (
            <optgroup label="Past due, by the week they were due">
              {pastWeeks.map(([w, list]) => <option key={w} value={w}>Due {weekLabel(w)} ({list.length})</option>)}
            </optgroup>
          )}
        </select>
        <button
          className="small" disabled={refreshing}
          onClick={async () => { setRefreshing(true); await refresh(); setRefreshing(false); }}
        >{refreshing ? 'Refreshing…' : 'Refresh'}</button>
      </div>
      <p className="sub">
        {show === 'open' ? 'Everything students can still work on, soonest due first.' : `Assignments that were due ${weekLabel(show)}.`}
        {' '}Open an assignment for its full results (scores, on time or late, extensions, CSV).
      </p>

      {cols.length === 0
        ? <div className="notice">{show === 'open' ? <>Nothing open right now. <Link to="/assign">Assign something</Link></> : 'Nothing past due yet.'}</div>
        : students.length === 0
          ? <div className="notice">No students yet — share the class code from the <Link to="/roster">roster</Link>.</div>
          : (
            <div className="table-wrap">
              <table className="grid">
                <thead>
                  <tr>
                    <th>Student</th>
                    {cols.map(a => (
                      <th key={a.id}>
                        <Link to={`/a/${a.id}`}>{KIND[a.kind].icon} {a.title}</Link>
                        <div className="small muted">
                          {students.filter(s => progressFor(a.id, s.user_id)?.completed_at).length}/{students.length} done
                          {a.due_date && ` · due ${shortDate(a.due_date)}`}
                        </div>
                        <div className="small muted">
                          {requirement(a, true)}
                          {a.kind === 'story' && a.story_text && ` · ${storyPlanLabel(a)}${storyModesUsed(a).some(needsReadingTime) ? ` (reading ${clock(minReadSeconds(a.story_text, a.read_wpm))})` : ''}`}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map(s => (
                    <tr key={s.user_id}>
                      <td>
                        <div className="term">{s.display_name}</div>
                        <div className="small muted">{s.username}</div>
                      </td>
                      {cols.map(a => <Cell key={a.id} a={a} due={dueFor(a, s.user_id)} p={progressFor(a.id, s.user_id)} passes={passesFor(a.id, s.user_id)} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );
}

function Cell({ a, p, passes, due }: { a: Assignment; p: Progress | undefined; passes: Pass[]; due: string | null }) {
  const late = due && due < today();
  if (!p) return <td className={`cell todo${late ? ' late' : ''}`}>{late ? 'Missing' : 'Not started'}</td>;

  const verb = KIND[a.kind].verb;
  const state = p.completed_at
    ? `✓ ${p.completions}/${a.required_count}`
    : `${p.completions}/${a.required_count} ${passWord(a.kind, 2)}`;

  return (
    <td className={`cell ${p.completed_at ? 'done' : 'started'}`}>
      <div className="state">{state}</div>
      {passes.length > 0 && (
        <div className="passes">
          {passes.map(x => (
            <span key={x.n} className="pass-chip" title={`${verb} ${x.n}: ${shortDate(x.finished_at)}${a.kind === 'story' ? `, ${x.lookups} lookups` : ''}`}>
              {x.total ? `${Math.round(((x.correct ?? 0) / x.total) * 100)}%` : clock(x.seconds)}
            </span>
          ))}
        </div>
      )}
      <div className="small muted">
        {a.kind === 'grammar'
          ? `${minutes(p.seconds_spent)} practising`
          : a.kind === 'video'
          ? `${minutes(p.video_watched)} played in total`
          : `${minutes(p.seconds_spent)} reading · ${p.lookups} lookup${p.lookups === 1 ? '' : 's'}`}
      </div>
      <div className="small muted">
        {p.visits} visit{p.visits === 1 ? '' : 's'} · last {shortDate(p.last_active_at)}
        {p.completed_at && ` · done ${shortDate(p.completed_at)}`}
      </div>
    </td>
  );
}

